/// <amd-module name="Wit/Common/BacklogContentService/IBacklogsHubContentService" />
import { IObservableValue, IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssHistoryService } from "VSS/Platform/History";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { IBacklogSettingsData } from "Wit/Common/BacklogContentService/BacklogContentDataProviderTypes";
import * as Resources from "Wit/Common/BacklogContentService/Resources";
import { ExceptionInfoResult } from "Wit/Common/HubError/Models/Models";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import { FilterManager } from "Wit/Filter/FilterManager";
import { BacklogColumnSettings } from "./BacklogContentContants";
import { IBacklogColumn, IBacklogData, IColumnPreference, ICommonBacklogData, IEffortData } from "./BacklogContentDataProviderTypes";
import { HubType, IObservableForecastSettings } from "./BacklogContentTreeTypes";
import { IBacklogWorkItemService } from "./IBacklogWorkItemService";
import { ICommonBacklogContentService, isExceptionInfoResult } from "./ICommonBacklogContentService";
import { IHubContentService } from "./IHubContentService";

export interface IBacklogsHubContentService extends IVssService, IHubContentService {
    getBacklogLevel(): IObservableValue<string>;
    getForecastSettings(): ObservableValue<IObservableForecastSettings | undefined>;
    getIsRequirementBacklog(): boolean;
    getIsRootBacklog(): boolean;
    resetForecastSettings(): void;
    updateBacklogLevel(): Promise<void>;
    updateForecastEffortData(effortData: IEffortData): void;
    updateForecastVelocityAndLines(velocity: number): void;
    getBacklogSettingsDataObservable(): IReadonlyObservableValue<IBacklogSettingsData | undefined>;
}

const backlogsHubSettingsKey = "Agile/BacklogsHub";
const forecastingVelocityKey = "SprintForecastVelocity";
const BacklogContentDataProvider = "ms.vss-work-web.new-backlogs-hub-backlog-data-provider";
const BacklogsFilterKey = "Backlogs/backlog/{0}/Filter";

class BacklogsHubContentService extends VssService implements IBacklogsHubContentService {
    private backlogLevel: IObservableValue<string>;
    private columnPreferences: IColumnPreference[];
    private commonBacklogContentService: ICommonBacklogContentService;
    private forecastSettings: ObservableValue<IObservableForecastSettings | undefined> = new ObservableValue(undefined);
    private isRequirementsBacklog = new ObservableValue(false);
    private isRootBacklog = new ObservableValue(false);
    private backlogSettingsData = new ObservableValue<IBacklogSettingsData | undefined>(undefined);

    public isReorderDisabled = false;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const currentLevelName = historyService.getState()["backlogLevel"];
        this.backlogLevel = new ObservableValue<string>(currentLevelName);

        this.commonBacklogContentService = this.pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
        this.commonBacklogContentService.setHubType(HubType.Backlogs);
        this.backlogLevel.subscribe(() => this.refreshTree());
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        super._serviceEnd(pageContext);
        this.backlogLevel.unsubscribe(() => this.refreshTree());
    }

    public getBacklogLevel = () => {
        return this.backlogLevel;
    };

    public getColumnPreferences = () => {
        return this.columnPreferences;
    };

    public getFilterKey = () => {
        return format(BacklogsFilterKey, this.backlogLevel.value);
    };

    public getForecastSettings = (): ObservableValue<IObservableForecastSettings | undefined> => {
        return this.forecastSettings;
    };

    public getIsRequirementBacklog = () => {
        return this.isRequirementsBacklog.value;
    };

    public getIsRootBacklog = () => {
        return this.isRootBacklog.value;
    };

    public getZeroDataPrimaryText = () => {
        return Resources.BacklogsZeroDataPrimaryText;
    };

    public getZeroDataSecondaryText = () => {
        return Resources.BacklogsZeroDataSecondaryText;
    };

    public getBacklogSettingsDataObservable = (): IReadonlyObservableValue<IBacklogSettingsData | undefined> => {
        return this.backlogSettingsData;
    };

    public onIterationChange() {
        return;
    }

    public settingsServiceGetColumns = async (): Promise<IBacklogColumn[]> => {
        return this.commonBacklogContentService.settingsServiceGetColumns(BacklogColumnSettings.ColumnsOptions);
    };

    public settingsServiceSetColumns = async (backlogColumns: IBacklogColumn[]): Promise<void> => {
        return this.commonBacklogContentService.settingsServiceSetColumns(BacklogColumnSettings.ColumnsOptionsKey, backlogColumns);
    };

    public settingsServiceResizeColumn = async (resizedColumnId: number, newWidth: number): Promise<void> => {
        return this.commonBacklogContentService.settingsServiceResizeColumn(BacklogColumnSettings.ColumnsOptions, resizedColumnId, newWidth);
    };

    public updateBacklogLevel = async (): Promise<void> => {
        const teamId = this.commonBacklogContentService.getCurrentTeam().id;

        const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
        const result: { [key: string]: any } = await settingsService.getEntriesAsync(
            `${BacklogColumnSettings.BacklogsHubKey}/${BacklogColumnSettings.BacklogId}`,
            SettingsUserScope.Me,
            BacklogColumnSettings.TeamScope,
            teamId
        );

        const lastBacklogLevel = result[teamId];
        if (lastBacklogLevel && this.backlogLevel.value !== lastBacklogLevel) {
            const historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
            const state = historyService.getState();
            state["backlogLevel"] = lastBacklogLevel;
            historyService.pushState(state);

            this.backlogLevel.value = lastBacklogLevel;
        }
    };

    public updateForecastEffortData = (effortData: IEffortData): void => {
        if (this.forecastSettings?.value) {
            this.forecastSettings.value.effortData = effortData;
        }
    };

    public updateForecastVelocityAndLines = (velocity: number): void => {
        if (this.forecastSettings?.value && this.forecastSettings.value.effortData) {
            if (this.forecastSettings.value.velocity.value !== velocity) {
                this.forecastSettings.value.velocity.value = velocity;
                this.settingsServiceSetVelocity();
            }
            this.updateForecastLines();
        }
    };

    public getBacklogContents = async (options?: { ignoreCache: boolean }): Promise<ICommonBacklogData | ExceptionInfoResult> => {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const result = await contributionService.getDataAsync<IBacklogData | ExceptionInfoResult>(
            BacklogContentDataProvider,
            {},
            options?.ignoreCache
        );

        if (isExceptionInfoResult(result)) {
            return result;
        } else {
            this.isRequirementsBacklog.value = !!result?.backlogPayload?.isRequirementBacklog;
            this.isRootBacklog.value = !!result?.backlogPayload?.isRootBacklog;
            this.columnPreferences = result?.backlogPayload.columnPreferences ?? [];

            if (result?.backlogPayload.forecastSettings) {
                const forecastSettings: IObservableForecastSettings = {
                    ...result!.backlogPayload.forecastSettings,
                    velocity: new ObservableValue(result!.backlogPayload.forecastSettings.velocity)
                };
                this.forecastSettings.value = forecastSettings;
                await this.settingsServiceGetVelocity();
            } else if (result && !result.backlogPayload.forecastSettings && this.forecastSettings?.value) {
                this.forecastSettings.value = undefined;
            }

            const backlogWorkItemTypeNames = result?.backlogPayload?.backlogContextWorkItemTypeNames ?? [];

            this.backlogSettingsData.value = {
                teamName: this.commonBacklogContentService.getCurrentTeam().name,
                teamId: this.commonBacklogContentService.getCurrentTeam().id,
                backlogLevelName: this.backlogLevel.value,
                automationRulesStates: result?.backlogPayload.automationRulesStates
            };

            return {
                backlogLevelId: result?.backlogPayload?.cumulativeFlowDiagramSettings?.backlogLevelId ?? "",
                backlogQueryResults: result?.backlogPayload?.queryResults,
                backlogWiql: result?.backlogPayload?.queryResults?.wiql ?? "",
                backlogWorkItemTypeNames: backlogWorkItemTypeNames,
                initialBacklogFilterJson: result?.initialBacklogFilterJson ?? "",
                columnPreferences: this.columnPreferences
            };
        }
    };

    private getWorkItemIdsForForecasting = (workItemEffort: { [key: number]: number }): number[] | undefined => {
        const backlogTree = this.commonBacklogContentService.getBacklogTree();
        const workItemIds = backlogTree.value?.rows.map(item => Number(item.data.id));

        if (workItemIds === undefined) {
            return undefined;
        }

        if (workItemIds && workItemIds.length > 0) {
            const workItemIdsMap = new Set<number>();
            for (const id of workItemIds) {
                if (workItemEffort.hasOwnProperty(id)) {
                    workItemIdsMap.add(id);
                }
            }

            for (const id of workItemIds) {
                if (workItemIdsMap.has(id)) {
                    const descendents = backlogTree.value?.allRows[id]?.children;
                    if (!descendents) {
                        continue;
                    }
                    for (const descendent of descendents) {
                        if (workItemIdsMap.has(descendent.id)) {
                            workItemIdsMap.delete(id);
                            break;
                        }
                    }
                }
            }
            return workItemIds.filter(id => workItemIdsMap.has(id));
        }

        return [];
    };

    public resetForecastSettings = () => {
        this.commonBacklogContentService.clearErrorMessage();
        this.forecastSettings.value = undefined;
        this.resetIterations();
    };

    private updateForecastLines = (): void => {
        this.resetIterations();

        const teamSettings = this.pageContext.getService<ITeamSettingsService>("ITeamSettingsService").getTeamSettingsObservable();
        const iterations = teamSettings.value?.futureIterations;
        const iterationCount = iterations?.length;
        let errorMessage = undefined;

        const workItemEffort: { [key: number]: number } = {};
        const effortData = this.forecastSettings.value!.effortData;
        for (let i = 0, l = effortData.ids.length; i < l; i++) {
            workItemEffort[effortData.ids[i]] = effortData.efforts[i];
        }

        const workItemIds = this.getWorkItemIdsForForecasting(workItemEffort);
        if (iterationCount === 0 || iterationCount === undefined) {
            errorMessage = Resources.Forecast_NoSprints;
        } else if (workItemIds && workItemIds.length === 0) {
            errorMessage = Resources.Forecast_NoBacklog;
        }

        // this is copied from SprintLineManager.ts in old backlogs code
        let size = this.forecastSettings.value!.velocity.value;
        let iterationIndex = 0;
        let effort = 0;
        let index = 0;
        let prevWorkItemId = 0;
        let totalEffort = 0; // effort included in the iteration so far

        while (iterationIndex < iterationCount! && workItemIds && index < workItemIds.length) {
            if (!workItemEffort.hasOwnProperty(workItemIds[index])) {
                index += 1;
                continue;
            }
            effort = workItemEffort[workItemIds[index]] || 0;
            if (effort + totalEffort <= size) {
                // current work item fit in the sprint
                totalEffort += effort;
                prevWorkItemId = workItemIds[index];
                index += 1;
            } else {
                // We have run out of space in the current iteration, so move to the next one.
                // Put the iteration on the previous work item
                const row = this.commonBacklogContentService.getBacklogTree().value?.allRows[workItemIds[index - 1]];
                if (row) {
                    row.iteration = iterations![iterationIndex].name;
                    row.value = row.value;
                }
                prevWorkItemId = 0;
                size = size - totalEffort + this.forecastSettings.value!.velocity.value; // size of new sprint will include the remaining effort not used by old sprint
                iterationIndex += 1;
                totalEffort = 0;
            }
        }

        if (!errorMessage && index === 0 && workItemIds) {
            errorMessage = Resources.Forecast_ItemTooBig;
        }

        // we could exit the loop because we ran out of Work Items so we need to add the last iteration
        if (iterationIndex < iterationCount! && totalEffort <= size && prevWorkItemId) {
            iterationIndex += 1;
        }
        if (errorMessage) {
            this.commonBacklogContentService.showErrorMessage(errorMessage);
        } else {
            this.commonBacklogContentService.clearErrorMessage();
        }
    };

    private resetIterations = () => {
        Object.entries(this.commonBacklogContentService.getBacklogTree().value?.allRows ?? {}).forEach(([id, row]) => {
            if (row.iteration) {
                row.iteration = undefined;
                row.value = row.value;
            }
        });
    };

    private settingsServiceGetVelocity = async () => {
        const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
        const result: { [key: string]: any } = await settingsService.getEntriesAsync(
            backlogsHubSettingsKey,
            SettingsUserScope.Me,
            BacklogColumnSettings.TeamScope,
            this.commonBacklogContentService.getCurrentTeam().id
        );
        const velocity = result[forecastingVelocityKey];

        if (velocity && this.forecastSettings?.value) {
            this.forecastSettings.value.velocity.value = velocity;
        }
    };

    private settingsServiceSetVelocity = async () => {
        const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
        const velocityKey = `${backlogsHubSettingsKey}/${forecastingVelocityKey}`;

        await settingsService.setEntries(
            { [velocityKey]: this.forecastSettings.value!.velocity.value },
            SettingsUserScope.Me,
            BacklogColumnSettings.TeamScope,
            this.commonBacklogContentService.getCurrentTeam().id
        );
    };

    public refreshTree = async (options?: { keepOpen?: boolean; filterManager?: FilterManager }) => {
        const backlogWorkItemService = this.pageContext.getService<IBacklogWorkItemService>("IBacklogWorkItemService");
        backlogWorkItemService.clearCache();

        const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
        const teamId = this.commonBacklogContentService.getCurrentTeam().id;
        const key = `${BacklogColumnSettings.BacklogsHubKey}/${BacklogColumnSettings.BacklogId}`;
        await settingsService.setEntries({ [key]: this.backlogLevel.value }, SettingsUserScope.Me, BacklogColumnSettings.TeamScope, teamId);
        this.commonBacklogContentService.refreshTree({
            getData: () => this.getBacklogContents({ ignoreCache: true }),
            keepOpen: options?.keepOpen,
            filterManager: options?.filterManager
        });
    };
}

Services.add("IBacklogsHubContentService", { serviceFactory: BacklogsHubContentService });
